import type {
  ArchivingRouteDto,
  RecordConfigForm,
  RecordConfigPayload,
  RecordConfigResponse,
  RecordFormat,
  SubjectSelection,
} from '../model/record-config.types';
import { buildArchivingRoutesPayload, routesFromPayload } from './archiving-path';
import {
  MAX_INDEXING_FIELDS,
  MAX_PROCEDURES,
  MIN_INDEXING_SEQUENCE_START,
} from './record-config-options';

export function hasElectronicFormat(format: RecordFormat | null): boolean {
  return format === 'ELECTRONIC' || format === 'ELECTRONIC_PHYSICAL';
}

export function hasPhysicalFormat(format: RecordFormat | null): boolean {
  return format === 'PHYSICAL' || format === 'ELECTRONIC_PHYSICAL';
}

export function hasArchivingPermission(format: RecordFormat | null): boolean {
  return format !== 'PHYSICAL';
}

export function emptySubjectSelection(): SubjectSelection {
  return { mode: 'NOT_APPLICABLE', positionIds: [], functionalActivityIds: [] };
}

function copySubjectSelection(selection: SubjectSelection): SubjectSelection {
  return {
    mode: selection.mode,
    positionIds: [...selection.positionIds],
    functionalActivityIds: [...selection.functionalActivityIds],
  };
}

function repeaterFrom(ids: number[]): Array<number | null> {
  return ids.length === 0 ? [null] : [...ids];
}

function compactRepeater(rows: Array<number | null>): number[] {
  return rows.filter((row): row is number => row !== null);
}

function routesByPosition(routes: ArchivingRouteDto[]): ArchivingRouteDto[] {
  return routes.map((route) => ({ ...route })).sort((a, b) => a.position - b.position);
}

export type IndexingPayload = Pick<
  RecordConfigPayload,
  'indexing' | 'indexingSequenceStart' | 'indexerFieldName' | 'indexingFieldNames'
>;

function chosenFieldNames(fieldNames: Array<string | null>): string[] {
  return fieldNames.filter((name): name is string => name !== null).slice(0, MAX_INDEXING_FIELDS);
}

export function indexingPayload(form: RecordConfigForm): IndexingPayload {
  const modeOnly: IndexingPayload = {
    indexing: form.indexing,
    indexingSequenceStart: null,
    indexerFieldName: null,
    indexingFieldNames: [],
  };
  switch (form.indexing) {
    case 'SEQUENTIAL':
      return { ...modeOnly, indexingSequenceStart: form.indexingSequenceStart };
    case 'NON_SEQUENTIAL':
      return { ...modeOnly, indexingFieldNames: chosenFieldNames(form.indexingFieldNames) };
    case 'OS_OF':
      return {
        ...modeOnly,
        indexerFieldName: form.indexerFieldName,
        indexingFieldNames: chosenFieldNames(form.indexingFieldNames),
      };
    default:
      return modeOnly;
  }
}

function subjectSelectionPayload(selection: SubjectSelection): SubjectSelection {
  if (selection.mode !== 'SELECT_SUBJECTS') {
    return { mode: selection.mode, positionIds: [], functionalActivityIds: [] };
  }
  return copySubjectSelection(selection);
}

export function createEmptyForm(): RecordConfigForm {
  return {
    title: '',
    hasStages: null,
    stageIds: [],
    procedureRevisionIds: [null],
    format: null,
    physicalLocationIds: [null],
    protectionMeanIds: [null],
    electronicArchivingModel: null,
    archivingRoutes: [],
    physicalArchivingModel: null,
    indexing: null,
    indexingSequenceStart: null,
    indexerFieldName: null,
    indexingFieldNames: [],
    retention: null,
    retentionMonths: null,
    retentionPeriods: [null],
    disposal: null,
    archivingPermission: emptySubjectSelection(),
    notification: emptySubjectSelection(),
  };
}

export function formFromResponse(response: RecordConfigResponse): RecordConfigForm {
  const { matrix, config } = response;
  return {
    title: matrix.title ?? '',
    hasStages: config.hasStages,
    stageIds: [...config.stageIds],
    procedureRevisionIds: repeaterFrom(config.procedureRevisionIds),
    format: config.format,
    physicalLocationIds: repeaterFrom(config.physicalLocationIds),
    protectionMeanIds: repeaterFrom(config.protectionMeanIds),
    electronicArchivingModel: config.electronicArchivingModel,
    archivingRoutes: routesByPosition(config.archivingRoutes),
    physicalArchivingModel: null,
    indexing: config.indexing,
    indexingSequenceStart: config.indexingSequenceStart,
    indexerFieldName: config.indexerFieldName,
    indexingFieldNames: [...config.indexingFieldNames],
    retention: config.retention,
    retentionMonths: config.retentionMonths,
    retentionPeriods: repeaterFrom(config.retentionPeriods),
    disposal: config.disposal,
    archivingPermission: copySubjectSelection(config.archivingPermission),
    notification: copySubjectSelection(config.notification),
  };
}

export function buildPayload(form: RecordConfigForm): RecordConfigPayload {
  const electronic = hasElectronicFormat(form.format);
  const physical = hasPhysicalFormat(form.format);
  const definedRetention = form.retention === 'DEFINED';

  return {
    title: form.title.trim(),
    hasStages: form.hasStages === true,
    stageIds: form.hasStages === true ? [...form.stageIds] : [],
    procedureRevisionIds: compactRepeater(form.procedureRevisionIds).slice(0, MAX_PROCEDURES),
    format: form.format,
    physicalLocationIds: physical ? compactRepeater(form.physicalLocationIds) : [],
    protectionMeanIds: compactRepeater(form.protectionMeanIds),
    electronicArchivingModel: electronic ? form.electronicArchivingModel : null,
    archivingRoutes: electronic
      ? buildArchivingRoutesPayload(
          form.electronicArchivingModel,
          routesFromPayload(form.archivingRoutes).map((fieldName) => ({ fieldName }))
        )
      : [],
    physicalArchivingModel: null,
    ...indexingPayload(form),
    retention: form.retention,
    retentionMonths: definedRetention ? form.retentionMonths : null,
    retentionPeriods: definedRetention ? compactRepeater(form.retentionPeriods) : [],
    disposal: form.disposal,
    archivingPermission: subjectSelectionPayload(form.archivingPermission),
    notification: subjectSelectionPayload(form.notification),
  };
}

function isSequenceStart(value: number | null): boolean {
  return value !== null && Number.isInteger(value) && value >= MIN_INDEXING_SEQUENCE_START;
}

function hasRequiredIndexing(form: RecordConfigForm): boolean {
  const { indexing, indexingSequenceStart, indexerFieldName, indexingFieldNames } =
    indexingPayload(form);
  switch (indexing) {
    case null:
      return false;
    case 'SEQUENTIAL':
      return isSequenceStart(indexingSequenceStart);
    case 'NON_SEQUENTIAL':
      return indexingFieldNames.length >= 1;
    case 'OS_OF':
      return (
        indexerFieldName !== null &&
        indexingFieldNames.length === MAX_INDEXING_FIELDS &&
        new Set([indexerFieldName, ...indexingFieldNames]).size === MAX_INDEXING_FIELDS + 1
      );
    default:
      return true;
  }
}

export function hasRequiredRecordConfigFields(
  form: RecordConfigForm,
  requiresIndexing: boolean
): boolean {
  if (form.title.trim() === '') return false;
  if (form.hasStages === null) return false;
  if (form.hasStages && form.stageIds.length === 0) return false;
  return !requiresIndexing || hasRequiredIndexing(form);
}

/**
 * Dirty tracking compares payloads, not forms, so an empty repeater row or trailing
 * whitespace in the title never counts as a change the user would be asked to discard.
 */
export function snapshotOf(form: RecordConfigForm): string {
  return JSON.stringify(buildPayload(form));
}
